// Cliente y modelo de la sección "Inscritos" (¿quién compite este fin de
// semana?). Consume el JSON público que publica el motor de datos; aquí solo
// lo tipamos, lo bajamos y lo aplanamos a filas para agrupar por prueba.
import { INSCRITOS_FINDE_URL } from '@/config/inscritos';
import { CANONICAL_INDEX } from '@/config/eventos';
import { isHigherBetter, parseMarkToNum } from '@/lib/marks';
import { canonPrueba } from '@/lib/pruebaCanon';

// ─────────────────────────── Forma del JSON del motor ───────────────────────
export interface InscritoRaw {
  atleta: string;
  club: string | null;
  marca: string | null;
  categoria: string | null; // categoría de la inscripción (por participación)
}

export interface PruebaInscritos {
  nombre: string | null; // nombre crudo de la prueba, tal cual lo da la federación
  sexo: string | null; // 'M' | 'F' | null
  categoria: string | null; // banda de edad de la prueba (Sub16, Absoluta, …)
  inscritos: InscritoRaw[];
}

export interface CompeticionInscritos {
  id: number;
  nombre: string;
  federacion: string | null;
  fecha_inicio: string | null; // ISO YYYY-MM-DD
  fecha_fin: string | null;
  lugar: string | null;
  comunidad: string | null;
  pruebas: PruebaInscritos[];
}

export interface InscritosFinde {
  generado_en: string;
  ventana: { desde: string; hasta: string };
  competiciones: CompeticionInscritos[];
}

// ─────────────────────────── Fila aplanada (para la UI) ─────────────────────
export interface InscritoRow {
  prueba: string; // etiqueta canónica para mostrar (mismo formato que el Ranking)
  pruebaKey: string; // clave canónica de agrupación (pruebaKey del Ranking)
  sexo: string; // 'M' | 'F' | '' (para el filtro de género)
  categoria: string; // categoría de la prueba (para el filtro)
  atleta: string;
  club: string | null;
  marca: string | null;
  competicion: string;
  federacion: string | null;
  fechaInicio: string | null;
  fechaFin: string | null;
  lugar: string | null;
  comunidad: string | null;
}

/** Aún no hay nada publicado: el motor no ha subido el finde todavía. Caso
 *  esperado, no un error. Supabase Storage devuelve 404 si falta el objeto y
 *  400 ("Bucket not found") si el bucket aún no existe (primera vez). La vista
 *  lo muestra como aviso, no en rojo. */
export class InscritosNoPublicados extends Error {}

/** Descarga el JSON de inscritos del finde. `no-store` para ver siempre la
 *  última publicación del cron (el objeto se sobrescribe en la misma URL). */
export async function fetchInscritosFinde(signal?: AbortSignal): Promise<InscritosFinde> {
  const res = await fetch(INSCRITOS_FINDE_URL, { signal, cache: 'no-store' });
  if (res.status === 404 || res.status === 400) {
    throw new InscritosNoPublicados('inscritos no publicados aún');
  }
  if (!res.ok) throw new Error(`No se pudo cargar inscritos (HTTP ${res.status}).`);
  return (await res.json()) as InscritosFinde;
}

/** Aplana el árbol comp→prueba→inscrito a filas, cada una con su competición
 *  (día + lugar). El nombre crudo de la federación se canoniza aquí (clave de
 *  agrupación + etiqueta con el formato del Ranking). Es lo que consumen los
 *  acordeones por prueba. */
export function flattenInscritos(data: InscritosFinde): InscritoRow[] {
  const rows: InscritoRow[] = [];
  for (const c of data.competiciones) {
    for (const p of c.pruebas) {
      const crudo = (p.nombre ?? '').trim() || '(sin prueba)';
      const { key, label } = canonPrueba(crudo);
      for (const i of p.inscritos) {
        rows.push({
          prueba: label,
          pruebaKey: key,
          sexo: p.sexo ?? '',
          categoria: (p.categoria ?? '').trim(),
          // Quita banderas del export entre paréntesis al final ("… (I)" =
          // invitado): rompen el matching de la ficha (normalizeName deja la
          // letra suelta) y no forman parte del nombre.
          atleta: i.atleta.replace(/\s*\([^)]*\)\s*$/, '').trim(),
          club: i.club,
          marca: i.marca,
          competicion: c.nombre,
          federacion: c.federacion,
          fechaInicio: c.fecha_inicio,
          fechaFin: c.fecha_fin,
          lugar: c.lugar,
          comunidad: c.comunidad,
        });
      }
    }
  }
  return rows;
}

export interface GrupoPrueba {
  key: string; // pruebaKey + sexo (clave estable del acordeón)
  pruebaKey: string; // clave canónica (para ordenar como el Ranking)
  prueba: string;
  sexo: string;
  count: number;
  rows: InscritoRow[];
}

/** Agrupa las filas por (clave canónica de prueba, sexo): la misma prueba se
 *  fusiona en un acordeón aunque cada federación la escriba distinto ("Disc
 *  Masculí" / "LLANÇAMENT DE DISC" / "Disco"). Orden: el MISMO orden canónico
 *  del programa que usa el Ranking (CANONICAL_INDEX): velocidad → fondo →
 *  vallas → obstáculos → saltos → lanzamientos → combinadas → marcha → relevos. */
export function groupByPrueba(rows: InscritoRow[]): GrupoPrueba[] {
  const map = new Map<string, GrupoPrueba>();
  for (const r of rows) {
    const key = `${r.pruebaKey}|${r.sexo}`;
    const g = map.get(key);
    if (g) {
      g.rows.push(r);
      g.count++;
    } else {
      map.set(key, {
        key, pruebaKey: r.pruebaKey, prueba: r.prueba, sexo: r.sexo, count: 1, rows: [r],
      });
    }
  }
  const orden = (g: GrupoPrueba) => CANONICAL_INDEX.get(g.pruebaKey) ?? 999;
  return [...map.values()].sort(
    (a, b) =>
      orden(a) - orden(b) ||
      a.prueba.localeCompare(b.prueba, 'es') ||
      a.sexo.localeCompare(b.sexo),
  );
}

/** Formatea la fecha (o rango) de competición como "sáb 11" o "11–12 jul". */
export function formatDiaCompeticion(inicio: string | null, fin: string | null): string {
  if (!inicio) return '—';
  const di = new Date(inicio + 'T00:00:00');
  const fmtDia = (d: Date) => d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric' });
  if (!fin || fin === inicio) return fmtDia(di);
  const df = new Date(fin + 'T00:00:00');
  const dia = (d: Date) => d.toLocaleDateString('es-ES', { day: 'numeric' });
  const mes = df.toLocaleDateString('es-ES', { month: 'short' });
  return `${dia(di)}–${dia(df)} ${mes}`;
}

// ─────────────────────────── Ordenación del detalle ────────────────────────
// Columnas ordenables del acordeón de una prueba.
export type SortKey = 'atleta' | 'club' | 'marca' | 'dia' | 'lugar' | 'competicion';
export type SortDir = 'asc' | 'desc';

/** Ordena las filas de un acordeón por la columna pedida. La marca usa el valor
 *  numérico de la prueba (mejor primero de fábrica: carreras menor tiempo,
 *  concursos mayor distancia); el día por fecha ISO; el resto alfabético. Los
 *  vacíos van SIEMPRE al final, sin importar la dirección. No muta la entrada. */
export function sortRows(
  rows: InscritoRow[], prueba: string, key: SortKey, dir: SortDir,
): InscritoRow[] {
  const mul = dir === 'asc' ? 1 : -1;
  const cmpTxt = (a: string | null, b: string | null) => {
    const A = (a ?? '').trim(), B = (b ?? '').trim();
    if (!A && !B) return 0;
    if (!A) return 1;
    if (!B) return -1;
    return A.localeCompare(B, 'es') * mul;
  };
  return [...rows].sort((a, b) => {
    switch (key) {
      case 'atleta': return cmpTxt(a.atleta, b.atleta);
      case 'club': return cmpTxt(a.club, b.club);
      case 'lugar': return cmpTxt(a.lugar, b.lugar);
      case 'competicion': return cmpTxt(a.competicion, b.competicion);
      case 'dia': return cmpTxt(a.fechaInicio, b.fechaInicio);
      case 'marca': {
        const A = parseMarkToNum(prueba, a.marca);
        const B = parseMarkToNum(prueba, b.marca);
        if (A === null && B === null) return 0;
        if (A === null) return 1;   // sin marca, al final
        if (B === null) return -1;
        // 'asc' = mejor marca primero (carreras menor tiempo; concursos mayor distancia)
        return (isHigherBetter(prueba) ? B - A : A - B) * mul;
      }
    }
  });
}
